//TODO: à tester
export class Rules {

    /**
     * <h3>Validation d'un déplacement</h3>
     * <p>Retourne <code>true</code> si le déplacement est autorisé, sinon <code>false</code>.</p>
     * <ul>
     * <li><b>Coordonnées</b> : source et destination doivent exister sur le Checkerboard.</li>
     * <li><b>Déplacement nul</b> : interdit.</li>
     * <li><b>Source</b> : on ne part jamais d'un pion au-delà du <code>layer</code> 1.</li>
     * <li><b>Empilement</b> : interdit si la destination a déjà un pion <code>layer</code> 2.</li>
     * <li><b>Empilement autorisé</b> : uniquement si la superposition donne du noir
     * (<code>source.couleur | dest.couleur === 7</code>) et si le pion en dessous est d'un autre joueur.</li>
     * <li><b>Couleurs primaires (1,2,4)</b> : une case en avant, à gauche ou à droite.</li>
     * <li><b>Couleurs secondaires (3,5,6)</b> : 1 ou 2 cases en avant, à gauche ou à droite, ou une case en diagonale avant.</li>
     * <li><b>Pas de saut</b> : pour un déplacement de 2 cases, la case intermédiaire doit être vide.</li>
     * <li><b>Noir (7)</b> : un pion noir ne peut pas être déplacé.</li>
     * </ul>
     *
     * @param {Checkerboard} checkerboard
     * @param {number} originX
     * @param {number} originY
     * @param {number} destX
     * @param {number} destY
     * @returns {boolean}
     */
    static isMovementDoable(checkerboard, originX, originY, destX, destY) {
        const originTile = checkerboard.getTile(originX, originY);
        const destTile = checkerboard.getTile(destX, destY);

        // Vérification des coordonnées et du déplacement nul
        if (!originTile || !destTile) {
            console.log("deplacement interdit raison : source ou destination hors du plateau");
            return false;
        }
        if (originX === destX && originY === destY) {
            console.log("deplacement interdit raison : source identique a destination");
            return false;
        }

        const originPawns = checkerboard.getAllPawnsOnTile(originX, originY);

        // Si un pion est au layer 2 (donc noir/empilé), on ne peut pas bouger (ni lui, ni celui du dessous)
        if (originPawns.some(pawn => pawn.layer === 2)) {
            console.log("deplacement interdit raison : pile source contient un layer 2");
            return false;
        }

        // On prend le pion du layer 1 comme source, s'il existe
        const originPawn = originPawns.find(pawn => pawn.layer === 1) || null;
        if (!originPawn) {
            console.log("deplacement interdit raison : aucun pion layer 1 a la source");
            return false;
        }

        // Vérification de l'empilement à la destination
        const destPawns = checkerboard.getAllPawnsOnTile(destX, destY);
        let destLayer1 = null;
        let hasDestLayer2 = false;
        for (const pawn of destPawns) {
            if (pawn.layer === 2) {
                hasDestLayer2 = true;
                break;
            }
            if (!destLayer1 && pawn.layer === 1) {
                destLayer1 = pawn;
            }
        }

        // Si la destination a déjà un pion au layer 2, le déplacement est interdit
        if (hasDestLayer2) {
            console.log("deplacement interdit raison : destination deja au layer 2");
            return false;
        }
        if (destLayer1) {
            const superposition = destLayer1.viewColor | originPawn.color;
            const isOpponent = destLayer1.player !== originPawn.player;
            if (superposition !== 7 || !isOpponent) {
                return false;
            }
        }

        // Calcul des déplacements possibles selon la couleur du pion source
        const directionY = originPawn.player === 1 ? 1 : -1;
        const dx = destX - originX;
        const dy = destY - originY;
        const absDx = Math.abs(dx);

        // Un pion noir (7) ne peut pas être déplacé
        const color = originPawn.viewColor;
        if (color === 7) {
            console.log("deplacement interdit raison : le pion noir ne peut pas bouger");
            return false;
        }

        // Couleurs primaires (1,2,4) : 1 case en avant, à gauche ou à droite
        const isPrimary = color === 1 || color === 2 || color === 4;
        if (isPrimary) {
            return (dx === 0 && dy === directionY) || (absDx === 1 && dy === 0);
        }

        // Couleurs secondaires (3,5,6) : mouvements plus flexibles
        const isSecondary = color === 3 || color === 5 || color === 6;
        if (!isSecondary) {
            console.log("deplacement interdit raison : couleur non supportee");
            return false;
        }

        const hasAnyPionAt = (x, y) => {
            const c = checkerboard.getTile(x, y);
            return c ? checkerboard.getAllPawnsOnTile(x, y).length > 0 : false;
        };

        if (absDx === 1 && Math.abs(dy) === 1) {
            const obstacle1 = hasAnyPionAt(originX + dx, originY);
            const obstacle2 = hasAnyPionAt(originX, originY + dy);

            if (obstacle1 && obstacle2) {
                console.log("Mouvement diagonal bloqué par les pions adjacents");
                return false;
            }
        }

        // 1 case (Orthogonal or Diagonal Front)
        if ((dx === 0 && dy === directionY) || (absDx === 1 && dy === 0) || (absDx === 1 && dy === directionY)) {
            return true;
        }

        // 2 cases (Orthogonal or Diagonal Front) - vérification du saut
        // 2 cases (Orthogonal only)
        if (dx === 0 && dy === 2 * directionY) {
            return !hasAnyPionAt(originX, originY + directionY);
        }
        if (absDx === 2 && dy === 0) {
            return !hasAnyPionAt(originX + (dx > 0 ? 1 : -1), originY);
        }

        return false;
    }

    /**
     * <h3>Vérification d'empilement (capture)</h3>
     * <p>Retourne <code>true</code> si l'état de la case est compatible avec une superposition valide.</p>
     * <ul>
     *   <li><b>Cas empilé</b> : si deux pions sont présents (layers 1 et 2), la superposition doit donner du noir
     *       (<code>couleurVue | couleurVue === 7</code>) et les joueurs doivent être différents.</li>
     *   <li><b>Cas non empilé</b> : si la case n'a pas deux pions, la condition est considérée valide.</li>
     * </ul>
     *
     * @param {Checkerboard} checkerboard
     * @param {number} x
     * @param {number} y
     * @returns {boolean}
     */
    static canEat(checkerboard, x, y) {
        const pawnLayer1 = checkerboard.getPawn(x, y, 1);
        const pawnLayer2 = checkerboard.getPawn(x, y, 2);

        if (!pawnLayer1 || !pawnLayer2) {
            return false;
        }

        const superposition = pawnLayer1.viewColor | pawnLayer2.viewColor;
        const isOpponent = pawnLayer1.player !== pawnLayer2.player;

        return (superposition === 7 && isOpponent);
    }

    /**
     * <h3>Vérification globale de la victoire</h3>
     * <p>Retourne <code>0</code> si pas de victoire, <code>1</code> si J1 gagne, <code>2</code> si J2 gagne.</p>
     * <ul>
     *   <li><b>Victoire passive</b> : alignement des pions primaires sur la ligne adverse.</li>
     *   <li><b>Victoire agressive</b> : capture de pions primaires adverses.</li>
     * </ul>
     *
     * @param {Checkerboard} checkerboard
     * @returns {number} 0 si pas de victoire, 1 si J1 gagne, 2 si J2 gagne.
     */
    static isWin(checkerboard) {
        const win1 = this.isPassiveWin(checkerboard);
        const win2 = this.isAggressiveWin(checkerboard);
        if (win1 === 1 || win2 === 1) return 1;
        if (win1 === 2 || win2 === 2) return 2;
        return 0;
    }

    /**
     * <h3>Victoire passive (alignement sur la ligne adverse)</h3>
     * <p>Retourne <code>0</code> si pas de victoire, <code>1</code> si J1 gagne, <code>2</code> si J2 gagne.</p>
     * <ul>
     *   <li><b>Primaires requises</b> : Cyan (1), Jaune (2), Magenta (4).</li>
     *   <li><b>Ligne adverse</b> : J1 vise <code>y = max</code>, J2 vise <code>y = 0</code>.</li>
     *   <li><b>Condition</b> : chaque pion primaire doit être sur une case de même couleur,
     *       et placé sur la ligne adverse.</li>
     * </ul>
     *
     * @param {Checkerboard} checkerboard
     * @returns {number} 0 si pas de victoire, 1 si J1 gagne, 2 si J2 gagne.
     */
    static isPassiveWin(checkerboard) {
        let maxY = -1;
        for (const checkerboardTile of checkerboard.tiles) {
            if (checkerboardTile.y > maxY) maxY = checkerboardTile.y;
        }
        if (maxY < 0) return 0;

        const p1Colors = new Set();
        const p2Colors = new Set();

        for (const pawn of checkerboard.pawns) {
            if (pawn.color !== 1 && pawn.color !== 2 && pawn.color !== 4) continue;
            const checkerboardTile = checkerboard.getTile(pawn.x, pawn.y);
            if (!checkerboardTile || checkerboardTile.color !== pawn.color) continue;
            if (pawn.player === 1 && pawn.y === maxY) {
                p1Colors.add(pawn.color);
            } else if (pawn.player === 2 && pawn.y === 0) {
                p2Colors.add(pawn.color);
            }
        }

        const p1Win = p1Colors.has(1) && p1Colors.has(2) && p1Colors.has(4);
        const p2Win = p2Colors.has(1) && p2Colors.has(2) && p2Colors.has(4);

        if (p1Win && !p2Win) return 1;
        if (p2Win && !p1Win) return 2;
        return 0;
    }

    /**
     * <h3>Victoire agressive (par captures)</h3>
     * <p>Retourne <code>0</code> si pas de victoire, <code>1</code> si J1 gagne, <code>2</code> si J2 gagne.</p>
     * <ul>
     *   <li><b>Primaires requises</b> : Cyan (1), Jaune (2), Magenta (4).</li>
     *   <li><b>Condition</b> : si un joueur n'a plus toutes les primaires, il perd.</li>
     * </ul>
     *
     * @param {Checkerboard} checkerboard
     * @returns {number} 0 si pas de victoire, 1 si J1 gagne, 2 si J2 gagne.
     */
    static isAggressiveWin(checkerboard) {
        const p1Colors = new Set();
        const p2Colors = new Set();

        for (const pawn of checkerboard.pawns) {
            if (pawn.color !== 1 && pawn.color !== 2 && pawn.color !== 4) continue;
            if (pawn.player === 1) {
                p1Colors.add(pawn.color);
            } else if (pawn.player === 2) {
                p2Colors.add(pawn.color);
            }
        }

        const p1HasAll = p1Colors.has(1) && p1Colors.has(2) && p1Colors.has(4);
        const p2HasAll = p2Colors.has(1) && p2Colors.has(2) && p2Colors.has(4);

        if (!p1HasAll && p2HasAll) return 2;
        if (!p2HasAll && p1HasAll) return 1;
        return 0;
    }

    static checkRepeat(history) {
        if (!history || history.length < 9) return false;

        const isSameMove = (a, b) =>
            a.sourceX === b.sourceX && a.sourceY === b.sourceY &&
            a.destX === b.destX && a.destY === b.destY;

        const checkPlayer = (p) => {
            const moves = history.filter(move => move.player === p).reverse();
            if (moves.length < 5) return false;
            return isSameMove(moves[0], moves[2]) && isSameMove(moves[2], moves[4]);
        };

        return checkPlayer(1) && checkPlayer(2);
    }

}